<script setup>
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import MyLoadsDesktopButtonComponent from './MyLoadsDesktopButtonComponent.vue';
import { useScreenSize } from '@/composables/useScreenSize';
import { capitalizeWords } from '@/common/textFormater';
import { getStopTime, getStopDay } from '@/utils/time';

const router = useRouter();
const { innerWidth } = useScreenSize();
const { load } = defineProps({
  load: {
    type: Object,
    required: true,
  },
  shouldWrap: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['showAssignVehicleModal']);

const activePageTab = computed(
  () => router.currentRoute.value.query.tab || 'unassigned'
);

const getStatusClass = (status) => {
  switch (status) {
    case 'progress':
      return 'bg-ocean-100 text-ocean-800';
    case 'covered':
      return 'bg-sun-50 text-sun-800';
    case 'cancelled':
      return 'bg-steel-100 text-steel-800';
    case 'unassigned':
      return 'bg-rose-100 text-rose-800';
    default:
      return 'bg-forest-100 text-forest-800';
    //   default case is green/good
  }
};

const stopDetails = (stop) => {
  if (!stop) {
    return false;
  }
  return {
    id: stop.id,
    street1: capitalizeWords(stop.street1),
    street2: capitalizeWords(stop.street2),
    city: capitalizeWords(stop.city) || stop.area_five_zip_city || '',
    state: stop.state || stop.area_five_zip_state || '',
    zip: stop.postal_code || '',
    appointmentDay: getStopDay(
      stop.scheduled_window_started_at_in_stop_timezone ||
        stop.scheduled_window_ended_at_in_stop_timezone,
      stop.timezone,
      { monthNumeric: true, yearDigits: 2, includesYear: false }
    ),
    appointmentTime: getStopTime(
      stop.scheduled_window_started_at_in_stop_timezone ||
        stop.scheduled_window_ended_at_in_stop_timezone,
      stop.timezone
    ),
    arrivalDay: getStopDay(stop.arrived_at_in_stop_timezone, stop.timezone),
    arrivalTime: getStopTime(stop.arrived_at_in_stop_timezone, stop.timezone),
    departureDay: getStopDay(stop.departed_at_in_stop_timezone, stop.timezone),
    departureTime: getStopTime(
      stop.departed_at_in_stop_timezone,
      stop.timezone
    ),
  };
};
const origin = computed(() =>
  load?.stops?.length
    ? stopDetails(load.stops[0])
    : { city: '-', appointmentDay: '-' }
);
const destination = computed(() =>
  load?.stops?.length > 1
    ? stopDetails(load.stops[load.stops.length - 1])
    : {
        city: '-',
        appointmentDay: origin.value.appointmentDay,
        appointmentTime: origin.value.appointmentTime,
      }
);
</script>

<template>
  <div
    v-if="innerWidth > 0"
    class="flex min-w-[285px] flex-col gap-2 rounded-xl bg-white px-4 py-3 outline outline-1 outline-offset-[-1px] outline-neutral-300-a"
    :class="
      innerWidth < 400 ? 'w-full' : innerWidth < 610 ? 'w-[400px]' : 'w-[285px]'
    "
  >
    <div class="flex w-full items-center justify-between gap-1 pb-2">
      <div
        class="text-color-text-neutral-boldest-default justify-start self-stretch font-['Poppins'] text-base font-semibold leading-normal tracking-tight"
      >
        {{ load?.shipment_identifier || '955595' }}
      </div>
      <div
        v-if="activePageTab !== 'unassigned'"
        class="flex items-center justify-center gap-1 rounded-full px-1.5 py-0.5 text-xs capitalize"
        :class="getStatusClass(load?.status)"
      >
        {{ load?.status?.replace('_', ' ') }}
      </div>
    </div>
    <div
      class="relative flex h-[125px] flex-col items-start justify-center gap-2 self-stretch pb-2"
    >
      <div class="flex items-center justify-start gap-1 self-stretch pb-1">
        <div class="relative h-6 w-6 overflow-hidden">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12 19.5C16.1421 19.5 19.5 16.1421 19.5 12C19.5 7.85786 16.1421 4.5 12 4.5C7.85786 4.5 4.5 7.85786 4.5 12C4.5 16.1421 7.85786 19.5 12 19.5ZM12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
              fill="#6C7C9D"
            />
          </svg>
        </div>
        <div class="flex flex-1 items-center justify-between">
          <div
            class="text-color-text-neutral-bold-default justify-start font-['Poppins'] text-sm font-medium leading-tight tracking-tight"
          >
            {{
              `${origin.city}${origin.city === '-' ? '' : origin.state ? `, ${origin.state}` : ''}`
            }}
          </div>
          <div
            class="text-color-text-neutral-subtle-default justify-start font-['Poppins'] text-xs font-normal leading-none tracking-tight"
          >
            {{
              `${origin.appointmentDay}${origin.appointmentTime ? `• ${origin.appointmentTime}` : ''}`
            }}
          </div>
        </div>
      </div>
      <img
        class="pl-2.5"
        src="@/assets/loads/line.svg"
        alt="line icon"
      />
      <div class="flex items-center justify-start gap-1 self-stretch">
        <div class="relative h-6 w-6 overflow-hidden">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M12 6.70117C10.2051 6.70117 8.75 8.15625 8.75 9.95117C8.75 11.7461 10.2051 13.2012 12 13.2012C13.7949 13.2012 15.25 11.7461 15.25 9.95117C15.25 8.15625 13.7949 6.70117 12 6.70117Z"
              fill="#6C7C9D"
            />
            <path
              fill-rule="evenodd"
              clip-rule="evenodd"
              d="M12 4.25C10.496 4.25 9.04001 4.8779 7.95622 6.01799L7.95622 6.01799C6.87039 7.16021 6.25 8.72234 6.25 10.3636C6.25 12.7482 7.7329 15.1099 9.42885 16.993C10.2549 17.9102 11.0847 18.6623 11.7089 19.1852C11.8122 19.2717 11.9095 19.3517 12 19.4249C12.0905 19.3517 12.1878 19.2717 12.2911 19.1852C12.9153 18.6623 13.7451 17.9102 14.5712 16.993C16.2671 15.1099 17.75 12.7482 17.75 10.3636C17.75 8.72232 17.1296 7.16022 16.0438 6.01803L16.0437 6.01798C14.96 4.8779 13.504 4.25 12 4.25ZM12 21C11.2823 22.0234 11.282 22.0232 11.2817 22.023L11.2809 22.0224L11.2788 22.021L11.2728 22.0167L11.2532 22.0028C11.2369 21.9912 11.2141 21.9748 11.1853 21.9538C11.1277 21.9118 11.0457 21.8512 10.943 21.7732C10.7377 21.6172 10.4486 21.3907 10.1036 21.1017C9.41534 20.5252 8.49513 19.692 7.57115 18.6661C5.7671 16.6629 3.75 13.7063 3.75 10.3636C3.75 8.09901 4.6046 5.91518 6.14429 4.29552L7.05025 5.15676L6.14429 4.29552C7.68601 2.67371 9.79095 1.75 12 1.75C14.209 1.75 16.3139 2.6737 17.8556 4.29549M12 21L12.7177 22.0234C12.2869 22.3255 11.7131 22.3255 11.2823 22.0234L12 21ZM17.8557 4.29553C19.3955 5.91521 20.25 8.09904 20.25 10.3636C20.25 13.7063 18.2329 16.6629 16.4288 18.6661C15.5049 19.692 14.5847 20.5252 13.8964 21.1017C13.5514 21.3907 13.2623 21.6172 13.057 21.7732C12.9543 21.8512 12.8723 21.9118 12.8147 21.9538C12.7859 21.9748 12.7631 21.9912 12.7468 22.0028L12.7272 22.0167L12.7212 22.021L12.7191 22.0224L12.7183 22.023C12.718 22.0232 12.7177 22.0234 12 21"
              fill="#6C7C9D"
            />
          </svg>
          <div
            class="bg-color-icon-status-neutral absolute left-[3.75px] top-[1.75px] h-5 w-4"
          ></div>
        </div>
        <div class="flex flex-1 items-center justify-between">
          <div
            class="text-color-text-neutral-bold-default justify-start font-['Poppins'] text-sm font-medium leading-tight tracking-tight"
          >
            {{
              `${destination.city}${destination.city === '-' ? '' : destination.state ? `, ${destination.state}` : ''}`
            }}
          </div>
          <div
            class="text-color-text-neutral-subtle-default justify-start font-['Poppins'] text-xs font-normal leading-none tracking-tight"
          >
            {{
              `${destination.appointmentDay}${destination.appointmentTime ? `• ${destination.appointmentTime}` : ''}`
            }}
          </div>
        </div>
      </div>
    </div>
    <div class="flex items-center justify-between gap-2 self-stretch">
      <MyLoadsDesktopButtonComponent
        button-class="flex-1 default-button rounded-[3px] px-3 py-1.5"
        button-text="View Details"
        text-class=" text-sm font-medium leading-10 tracking-tight whitespace-nowrap"
        @button-click="
          () => {
            router.push({
              name: 'Load Detail',
              params: { id: load.id },
            });
          }
        "
      />
      <MyLoadsDesktopButtonComponent
        v-if="activePageTab === 'unassigned'"
        :button-class="`${load.carrier_vehicle?.id ? 'secondary-button' : 'primary-button'} flex-1 rounded-[3px] px-3 py-1.5`"
        :button-text="`${load.carrier_vehicle?.id ? 'Confirm' : 'Assign'} Vehicle`"
        text-class="text-sm font-medium leading-10 tracking-tight whitespace-nowrap"
        @button-click="
          (e) => {
            e.stopPropagation();
            emit('showAssignVehicleModal', load, origin, destination);
          }
        "
      />
    </div>
  </div>
</template>

<style lang="scss" scoped></style>
